/* Shared, display-only promotion evaluation. Cart mutations stay in the native cart controller. */
(function (scope) {
  'use strict';
  const integer = value => (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') && Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;
  function roundUp(rawCents, stepUnits = 5) {
    const raw = integer(rawCents), step = Number(stepUnits) * 100;
    if (raw === null || !Number.isSafeInteger(step) || step <= 0) return 0;
    const result = Math.ceil(raw / step) * step;
    return Number.isSafeInteger(result) ? result : 0;
  }
  const unique = gifts => gifts.filter((g,i,a) => g && g.variantId && a.findIndex(x => String(x?.variantId) === String(g.variantId)) === i);
  function rewardInfo(result) {
    const fixed = unique(result?.fixedGifts || []).filter(g => g.available === true);
    const choices = unique(result?.choiceGifts || []).filter(g => g.available === true);
    const count = fixed.length + (result?.splitChoices ? result.choiceGroups.reduce((sum,group) => sum + Math.min(group.limit,choices.filter(g => group.variantIds.includes(String(g.variantId))).length),0) : Math.min(result?.choiceLimit || 0, choices.length));
    const known = count > 0 && [...fixed, ...choices].every(g => integer(g.valueCents) !== null);
    const best = result?.splitChoices ? result.choiceGroups.flatMap(group => choices.filter(g => group.variantIds.includes(String(g.variantId))).sort((a,b) => b.valueCents-a.valueCents).slice(0,group.limit)) : choices.slice().sort((a,b) => b.valueCents-a.valueCents).slice(0,result?.choiceLimit || 0);
    const value = known ? [...fixed,...best].reduce((sum,g) => sum + Number(g.valueCents),0) : null;
    return {fixed, choices, count, value};
  }
  function choiceCopy(program, result) {
    const multiple = result.choiceLimit > 1, premium = Number(result.minQuantity) >= 2;
    return {
      title: multiple ? 'Choose your FREE gifts' : premium ? 'Choose 1 FREE premium gift' : 'Choose 1 FREE gift',
      note: multiple ? 'Choose your gifts in the cart.' : premium ? 'Choose 1 FREE gift from the full selection—or keep your original choice.' : 'Choose 1 gift in your cart.',
      compact: multiple ? 'Choose your FREE gifts' : premium ? 'Choose 1 FREE premium gift' : 'Choose 1 FREE gift',
      unlocked: multiple ? 'Your FREE gifts are unlocked' : premium ? 'Full gift selection unlocked' : '1 FREE gift unlocked'
    };
  }
  function evaluate(program, quantity, subtotalUsdCents = program?.eligibleSubtotalUsdCents) {
    const count = integer(quantity);
    if (program?.version !== 2 || program.enabled !== true || count === null) return null;
    let tier = null;
    for (const candidate of Array.isArray(program.tiers) ? program.tiers : []) {
      const min = integer(candidate.minQuantity);
      if (candidate.enabled !== true || min === null || min < 1 || min > count) continue;
      if (!tier || min > Number(tier.minQuantity)) tier = candidate;
    }
    if (!tier) return null;
    let fixedGifts = Array.isArray(tier.fixedGifts) ? tier.fixedGifts : [];
    let choiceGifts = Array.isArray(tier.choiceGifts) ? tier.choiceGifts : [];
    let choiceLimit = tier.choiceLimit === 1 ? 1 : 0;
    if (program.choiceMode === 'unlocked') {
      choiceLimit += fixedGifts.length;
      choiceGifts = unique((program.tiers || []).filter(t => t.enabled === true && Number(t.minQuantity) > 0 && Number(t.minQuantity) <= Number(tier.minQuantity)).flatMap(t => [...(t.fixedGifts || []), ...(t.choiceGifts || [])]));
      const basic = unique(program.basicGifts || []);
      if (basic.some(g => choiceGifts.some(c => String(c.variantId) === String(g.variantId)))) choiceGifts = unique([...basic, ...choiceGifts]);
      fixedGifts = [];
    }
    const usdAmount = integer(subtotalUsdCents);
    const withinAmount = gift => !(integer(gift.minimumSubtotalUsdCents) > 0) || usdAmount !== null && usdAmount >= Number(gift.minimumSubtotalUsdCents);
    fixedGifts = fixedGifts.filter(withinAmount);
    choiceGifts = choiceGifts.filter(withinAmount);
    const splitChoices = program.choiceMode === 'unlocked' && Number(tier.minQuantity) >= 3 && choiceLimit === 2;
    const basicIds = new Set((program.basicGifts || []).map(g => String(g.variantId)));
    const choiceGroups = splitChoices ? [{id:'standard',limit:1,variantIds:choiceGifts.filter(g => basicIds.has(String(g.variantId))).map(g => String(g.variantId))},{id:'premium',limit:1,variantIds:choiceGifts.filter(g => !basicIds.has(String(g.variantId))).map(g => String(g.variantId))}] : [{id:'unified',limit:choiceLimit,variantIds:choiceGifts.map(g => String(g.variantId))}];
    return {
      splitChoices, choiceGroups,
      minQuantity: Number(tier.minQuantity), eligibleSubtotalUsdCents: usdAmount,
      key: tier.key, tierId: tier.tierId, eligibleQuantity: count,
      fixedGifts,
      choiceGifts,
      choiceLimit,
      giftCardRawCents: integer(tier.giftCardRawCents) || 0,
      giftCardValueCents: roundUp(tier.giftCardRawCents, program.roundingStep || 5),
      heading: tier.heading || '', choicePrompt: tier.choicePrompt || '',
      currency: program.currency, locale: program.locale
    };
  }
  function project(program, additionalEligibleQty, items) {
    const current = integer(program?.eligibleQuantity), added = integer(additionalEligibleQty);
    if (current === null || added === null || !Number.isSafeInteger(current + added)) return null;
    let subtotal = integer(program?.eligibleSubtotalUsdCents);
    if(Array.isArray(items))for(const item of items){const unit=integer(program?.usdPrices?.[String(item.variant_id??item.id)]),quantity=integer(item.quantity);if(subtotal===null||unit===null||quantity===null){subtotal=null;break;}subtotal+=unit*quantity;if(!Number.isSafeInteger(subtotal)){subtotal=null;break;}}
    return evaluate(program, current + added, subtotal);
  }
  const api = {evaluate, project, roundUp, rewardInfo, choiceCopy};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.document || scope.SissiluxPromotionRules) return;
  const document = scope.document;
  const parse = node => { try { return JSON.parse(node?.textContent || 'null'); } catch (_) { return null; } };
  const valid = value => value?.version === 2;
  function readProgram(context) {
    const localNode = context?.querySelector?.('[data-siss-promotion-program]');
    const local = parse(localNode);
    let current = null;
    for (const node of document.querySelectorAll('cart-drawer [data-gpt-gift-state]')) {
      const program = parse(node)?.promotion;
      if (valid(program)) { current = program; break; }
    }
    if (!current) {
      if (valid(local)) return local;
      for (const node of document.querySelectorAll('[data-siss-promotion-program]')) {
        const program = parse(node);
        if (valid(program)) return program;
      }
      return null;
    }
    // Product eligibility is local to this purchase surface. Cart count and
    // tier/currency data always come from the latest confirmed cart program.
    const matching = valid(local) && local.campaignId === current.campaignId && String(local.revision) === String(current.revision) && local.country === current.country;
    return context ? {...current, productEligible: matching && local.productEligible === true, contextCurrent: matching} : current;
  }
  function notify() { document.dispatchEvent(new CustomEvent('sissilux:promotion-updated')); }
  scope.SissiluxPromotionRules = {...api, readProgram};
  document.addEventListener('sissilux:bfcm-state', notify);
  document.addEventListener('shopify:section:load', notify);
  scope.addEventListener('pageshow', notify);
  if (scope.MutationObserver) new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-gpt-gift-state],[data-siss-promotion-program]') || node.querySelector?.('[data-gpt-gift-state],[data-siss-promotion-program]'))))) notify();
  }).observe(document.documentElement, {childList: true, subtree: true});
  notify();
})(typeof window !== 'undefined' ? window : globalThis);
